import { rolesColors } from '@/pages/UsersManagement/UsersManagement.constants';
import TagSelectorDropdown, { TagSelectorType } from '@/trio-ui/TagSelector';
import { TagSelectorOption } from '@/trio-ui/TagSelector/TagSelectorDropDown.component';
import { AvailableRoles, OptionsWithLabel, formattedRoles } from '@/types';

type Props = Pick<
  TagSelectorType,
  'name' | 'isLabelAboveField' | 'label' | 'selectorLimit' | 'errorMessage'
> & {
  value?: AvailableRoles[];
  onChange?: (roles: AvailableRoles[]) => void;
};

export const displayFormattedRoles: { [role in AvailableRoles]: string } = {
  [AvailableRoles.ACADEMY_ADMIN]: `Academy ${formattedRoles[AvailableRoles.ACADEMY_ADMIN]}`,
  [AvailableRoles.ACADEMY_USER]: `Academy ${formattedRoles[AvailableRoles.ACADEMY_USER]}`,
  [AvailableRoles.FREELANCE_REVIEWER]: formattedRoles[AvailableRoles.FREELANCE_REVIEWER],
  [AvailableRoles.FREELANCE_SOURCER]: formattedRoles[AvailableRoles.FREELANCE_SOURCER],
  [AvailableRoles.HIRING_ADMIN]: `Hiring ${formattedRoles[AvailableRoles.HIRING_ADMIN]}`,
  [AvailableRoles.HIRING_USER]: `Hiring ${formattedRoles[AvailableRoles.HIRING_USER]}`,
  [AvailableRoles.INSIDE_REVIEWER]: formattedRoles[AvailableRoles.INSIDE_REVIEWER],
  [AvailableRoles.INSIDE_SOURCER]: formattedRoles[AvailableRoles.INSIDE_SOURCER],
  [AvailableRoles.PEOPLE_ADMIN]: `People ${formattedRoles[AvailableRoles.PEOPLE_ADMIN]}`,
  [AvailableRoles.PEOPLE_USER]: `People ${formattedRoles[AvailableRoles.PEOPLE_USER]}`,
  [AvailableRoles.SEO_ADMIN]: `SEO ${formattedRoles[AvailableRoles.SEO_ADMIN]}`,
  [AvailableRoles.SEO_USER]: `SEO ${formattedRoles[AvailableRoles.SEO_USER]}`,
  [AvailableRoles.FINANCE_ADMIN]: `Finance ${formattedRoles[AvailableRoles.FINANCE_ADMIN]}`,
  [AvailableRoles.FINANCE_USER]: `Finance ${formattedRoles[AvailableRoles.FINANCE_USER]}`,
};

const createOptionFromRole = (role: AvailableRoles): TagSelectorOption => ({
  value: role,
  label: formattedRoles[role],
  displayLabel: displayFormattedRoles[role],
  backgroundColor: rolesColors[role],
});

const options: OptionsWithLabel<TagSelectorOption>[] = [
  {
    label: 'People',
    options: [AvailableRoles.PEOPLE_ADMIN, AvailableRoles.PEOPLE_USER].map(createOptionFromRole),
  },
  {
    label: 'Hiring',
    options: [
      AvailableRoles.HIRING_ADMIN,
      AvailableRoles.HIRING_USER,
      AvailableRoles.INSIDE_REVIEWER,
      AvailableRoles.INSIDE_SOURCER,
      AvailableRoles.FREELANCE_REVIEWER,
      AvailableRoles.FREELANCE_SOURCER,
    ].map(createOptionFromRole),
  },
  {
    label: 'Academy',
    options: [AvailableRoles.ACADEMY_ADMIN, AvailableRoles.ACADEMY_USER].map(createOptionFromRole),
  },
  {
    label: 'SEO',
    options: [AvailableRoles.SEO_ADMIN, AvailableRoles.SEO_USER].map(createOptionFromRole),
  },
  {
    label: 'Finance',
    options: [AvailableRoles.FINANCE_ADMIN, AvailableRoles.FINANCE_USER].map(createOptionFromRole),
  },
];

const RoleSelector = ({ value, onChange, ...tagSelectorProps }: Props): JSX.Element => {
  return (
    <TagSelectorDropdown.Grouped
      defaultValue={value}
      onChangeDropDown={onChange}
      options={options}
      {...tagSelectorProps}
    />
  );
};

export default RoleSelector;
